import { FC, useEffect, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import ActiveSwitch from "src/Pages/Company/Absences/Forms/ActiveSwitch";
import ConfigOption from "src/Pages/Company/Absences/Forms/ConfigOption";
import CustomRadioGroup from "src/Pages/Company/Absences/Forms/CustomRadioGroup";
import Title from "src/Pages/Company/Absences/Title/Title";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import {
  transformCustomFieldFromDTO,
  transformCustomFieldToDTO,
} from "src/utils/Company/Absences/transformCustomFields";
import translateLabels from "src/utils/Translations/translateLabels";
import useNativeConfirmBeforeUnload from "src/utils/jsHelpers/useNativeConfirmBeforeUnload";
import { useSaveAbsenceType } from "src/Pages/Company/Absences/api/useAbsenceTypes";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import CustomFieldsConfigurator from "src/Pages/Company/Absences/AbsenceType/AbsenceTypeForm/CustomFieldsConfigurator";
import ApproversConfiguration from "src/Pages/Company/Absences/AbsenceType/ApproversConfiguration";
import {
  EEntitledEmployeesType,
  EPresenceType,
  IAbsenceType,
  IAbsenceTypeForm,
  ICustomField,
  ICustomFieldDTO,
  NotificationType,
  ApproversType,
  BillingCycle,
  Hierarchy,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import BillingCycleConfiguration from "./BillingCycleConfiguration";
import EntitledEmployees from "./EntitledEmployees";
import SelectParentAbsenceType from "./SelectParentAbsenceType";
import SelectAbsenceGroup from "./SelectAbsenceGroup";
import {
  useConfigurationOptions,
  defaultEntitledEmployees,
  defaultNotificationsConfig,
} from "./configurationOptions";
import NotificationsConfiguration from "./NotificationsConfiguration";
import { useAbsenceTypeSchema } from "./AbsenceTypeForm.schema";

interface IAbsenceTypeFormProps {
  absenceTypeId: string | undefined;
  absenceTypeData?: IAbsenceType;
  listPath: string;
}

const AbsenceTypeForm: FC<IAbsenceTypeFormProps> = ({
  absenceTypeId,
  absenceTypeData,
  listPath,
}) => {
  const history = useHistory();
  const companyId = useCompanyId();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();
  const configurationOptions = useConfigurationOptions();
  const absenceTypeSchema = useAbsenceTypeSchema();

  if (!absenceTypeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type ID provided.",
      file: "AbsenceTypeForm.tsx",
    });
  }

  const [customFields, setCustomFields] = useState<ICustomField[]>(
    transformCustomFieldFromDTO(
      absenceTypeData?.customFieldsConfiguration?.customFields || []
    )
  );

  useNativeConfirmBeforeUnload();

  const absenceTypeMutation = useSaveAbsenceType(
    absenceTypeId,
    !!absenceTypeData?.fullyEditable
  );

  const onSubmit = (formData: IAbsenceTypeForm) => {
    if (formData.entitledEmployees.type === EEntitledEmployeesType.Explicit) {
      const entitledEmployeesFields = [
        "employmentTypes",
        "employeeGroups",
        "organizationUnits",
        "costPlaces",
      ] as const;

      const hasAnyEntitledEmployeeOption = entitledEmployeesFields.some(
        (field) => formData.entitledEmployees[field]?.length > 0
      );

      if (!hasAnyEntitledEmployeeOption) {
        formMethods.setError("entitledEmployees.type", {
          type: "manual",
          message: __("Wybór przynajmniej jednej z opcji jest wymagany"),
        });

        dynamicNotification(
          __("Wybierz przynajmniej jedną opcję w sekcji 'Dostępność wniosku'"),
          "error",
          "top-right",
          7000
        );

        return;
      }

      // Parse entitledEmployees fields to ensure they are empty arrays if no value is selected
      entitledEmployeesFields.forEach((field) => {
        // eslint-disable-next-line no-param-reassign
        formData.entitledEmployees[field] ??= [];
      });
    }

    const absenceTypeDTO: IAbsenceType = {
      ...formData,
      billingCycleConfiguration:
        // eslint-disable-next-line no-nested-ternary
        formData.billingCycle === BillingCycle.NONE
          ? null
          : {
              ...formData.billingCycleConfiguration,
              parentAbsenceTypeId:
                formData.billingCycleConfiguration?.parentAbsenceTypeId ?? null,
              type: formData.billingCycleConfiguration?.type ?? null,
              patterns: absenceTypeData?.fullyEditable
                ? formData.billingCycleConfiguration?.patterns.map((item) => ({
                    value: item.value,
                  })) || []
                : formData.billingCycleConfiguration?.patterns || [],
              stackUnusedTimeForNextBillingCycle:
                formData.billingCycleConfiguration
                  ?.stackUnusedTimeForNextBillingCycle || false,
            },
      approvers:
        formData.approversType === ApproversType.AUTO ? [] : formData.approvers,
    };

    if (customFields.length > 0) {
      absenceTypeDTO.customFieldsConfiguration = { customFields: [] };

      const customFieldsDTO: ICustomFieldDTO[] = customFields.map(
        transformCustomFieldToDTO
      );

      absenceTypeDTO.customFieldsConfiguration.customFields = customFieldsDTO;
    } else {
      absenceTypeDTO.customFieldsConfiguration = null;
    }

    absenceTypeMutation.mutate(absenceTypeDTO, {
      onSuccess: () => {
        dynamicNotification(__("Pomyślnie zapisano dane"));

        // A bit of a hack to ensure all state updates are done before redirecting
        // Used for confirmation modal
        setTimeout(() => {
          history.push(listPath);
        }, 500);
      },
      onError: () => {
        dynamicNotification(
          __("Nie udało się zapisać danych"),
          "error",
          "top-right",
          7000
        );
      },
    });
  };

  const formMethods = useForm<IAbsenceTypeForm>({
    resolver: standardSchemaResolver(absenceTypeSchema),
    mode: "onChange",
    defaultValues: absenceTypeData
      ? {
          ...absenceTypeData,
          hierarchy: absenceTypeData.billingCycleConfiguration
            ?.parentAbsenceTypeId
            ? Hierarchy.CHILD
            : Hierarchy.MAIN,
          billingCycle: absenceTypeData.billingCycleConfiguration
            ? BillingCycle.MONTHLY
            : BillingCycle.NONE,
          billingCycleConfiguration: {
            ...absenceTypeData.billingCycleConfiguration,
          },
          approversType: absenceTypeData.approvers?.length
            ? ApproversType.MANUAL
            : ApproversType.AUTO,
        }
      : {
          ownerId: companyId,
          active: true,
          presenceType: EPresenceType.Absence,
          approvers: [],
          entitledEmployees: defaultEntitledEmployees,
          notificationsConfiguration: defaultNotificationsConfig,
        },
  });

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    watch,
    setValue,
  } = formMethods;

  const requestAvailability = watch(
    "entitledEmployees.type",
    EEntitledEmployeesType.All
  );

  const chosenParentAbsenceType = watch(
    "billingCycleConfiguration.parentAbsenceTypeId"
  );

  const approversType = useWatch({
    control,
    name: "approversType",
  });

  useEffect(() => {
    // preserve edited AbsenceType configuration
    if ((absenceTypeData && !isDirty) || chosenParentAbsenceType) return;

    if (approversType === ApproversType.AUTO) {
      setValue("notificationsConfiguration", defaultNotificationsConfig);
    } else {
      setValue("notificationsConfiguration", {
        ...defaultNotificationsConfig,
        approverNotifications: {
          absenceRequestCreation: [NotificationType.EMAIL],
          absenceRequestEdition: [NotificationType.EMAIL],
        },
        immediateSuperiorAndApproverNotifications: {
          differentApproverAbsenceRequestAcceptation: [NotificationType.EMAIL],
          differentApproverAbsenceRequestAutoAcceptation: [],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approversType, setValue]);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid="absence-type-form"
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Title
            title={
              absenceTypeId
                ? __("Edycja szablonu wniosku")
                : __("Tworzenie nowego szablonu wniosku")
            }
          />
        </Box>
        {!canWriteAbsencesConfiguration && <PermissionsAlert />}

        <Paper sx={{ p: "2rem" }}>
          <Fieldset legend={__("Widoczność szablonu")}>
            <ActiveSwitch
              name="active"
              defaultValue
              disabled={!canWriteAbsencesConfiguration}
              data-testid="absence-type-form-field__active"
            />
          </Fieldset>
          <Fieldset legend={__("Informacje ogólne")}>
            <ControlledTextField
              name="name"
              label={__("Nazwa wniosku")}
              required
              fullWidth
              maxLength={100}
              disabled={!canWriteAbsencesConfiguration}
              data-testid="absence-type-form-field__name"
              normalizeSpaces
            />
            <ControlledTextField
              name="description"
              label={__("Opis")}
              multiline
              maxRows={3}
              maxLength={255}
              disabled={!canWriteAbsencesConfiguration}
              data-testid="absence-type-form-field__description"
            />
          </Fieldset>
          <CustomRadioGroup
            id="presenceType"
            name="presenceType"
            label={__("Rodzaj szablonu")}
            tooltip={__(
              "Pozwoli to na rozróżnienie wniosków dotyczących nieobecności, takich jak urlopy wypoczynkowe, od pracy wykonywanej poza biurem, np.: delegacji służbowych i pracy zdalnej (Home Office)."
            )}
            options={translateLabels(presenceTypeOptions)}
            disabled={!canWriteAbsencesConfiguration}
            dataTestId="absence-type-form-field__presence-type"
          />
          <Divider sx={{ my: "1rem" }} />
          <SelectParentAbsenceType
            absenceTypeId={absenceTypeId}
            fullyEditable={
              absenceTypeData?.fullyEditable && !absenceTypeData?.isMain
            }
          />
          <Divider sx={{ mt: "1.5rem", mb: "1rem" }} />
          <SelectAbsenceGroup
            disabled={
              !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
            }
          />
          <BillingCycleConfiguration
            fullyEditable={absenceTypeData?.fullyEditable}
            parentAbsenceType={chosenParentAbsenceType}
          />
          <CustomRadioGroup
            id="requestAvailability"
            name="entitledEmployees.type"
            label={__("Odbiorcy szablonu")}
            options={translateLabels(entitledEmployeesTypeOptions)}
            defaultValue={EEntitledEmployeesType.All}
            disabled={
              !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
            }
            dataTestId="absence-type-form-field__request-availability"
          />
          {requestAvailability === EEntitledEmployeesType.Explicit && (
            <EntitledEmployees
              disabled={
                !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
              }
            />
          )}
          <Divider sx={{ my: "1rem" }} />
          <ApproversConfiguration
            disabled={
              !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
            }
          />
          <Divider
            sx={{
              margin: "1rem 0 2rem",
            }}
          />
          <Typography variant="subtitle1">{__("Cechy szablonu")}</Typography>
          {configurationOptions.map((option) => (
            <ConfigOption
              key={option.id}
              {...option}
              name={`configurationOptions.${option.name}`}
              disabled={!canWriteAbsencesConfiguration}
              parentAbsenceType={chosenParentAbsenceType}
              dataTestId={`absence-type-form-field__${option.id}`}
            />
          ))}
          <NotificationsConfiguration
            data-testid="absence-type-notifications-configuration"
            disabled={
              !canWriteAbsencesConfiguration || !!chosenParentAbsenceType
            }
          />
          <CustomFieldsConfigurator
            customFields={customFields}
            setCustomFields={setCustomFields}
          />
          {canWriteAbsencesConfiguration && (
            <Box
              sx={{
                textAlign: "right",
                margin: "2rem 0 0 1rem",
              }}
              data-testid="absence-type-form-actions"
            >
              <Button
                variant="outlined"
                type="button"
                size="large"
                sx={{ marginRight: "1rem" }}
                onClick={() => history.push(listPath)}
                disabled={absenceTypeMutation.isPending}
              >
                {__("Anuluj")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                size="large"
                disabled={absenceTypeMutation.isPending}
                data-testid="absence-type-form__button-submit"
              >
                {__("Zapisz wniosek")}
              </Button>
            </Box>
          )}
        </Paper>
      </form>
      <Prompt
        when={isDirty && !absenceTypeMutation.isSuccess}
        message={__("Niezapisane zmiany zostaną utracone.")}
      />
    </FormProvider>
  );
};

const entitledEmployeesTypeOptions = [
  { label: "Wszyscy pracownicy", value: EEntitledEmployeesType.All },
  { label: "Wybrani pracownicy", value: EEntitledEmployeesType.Explicit },
];

const presenceTypeOptions = [
  { label: "Nieobecność", value: EPresenceType.Absence },
  {
    label: "Status obecności",
    value: EPresenceType.Attendance,
  },
];

export default AbsenceTypeForm;
